import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Autocomplete from '@mui/material/Autocomplete';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

interface VendorDetails {
  vendor_id: string;
  vendor_name: string;
  location: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
}

interface VendorInvoice {
  id: number;
  vendor_name: string;
  invoice_number: string;
  invoice_date: string;
  total_amount: number;
  account_number?: string;
  notes?: string;
  line_items?: any[];
  raw_extracted_text?: string;
  textract_response?: any;
  pdf_url?: string;
  pdf_filename?: string;
  validation_status?: string;
  servman_matched?: boolean;
  docuware_matched?: boolean;
  discrepancies?: string[];
  servman_data?: any;
  docuware_data?: any;
  validated_at?: string;
  location?: string;
  vendor_id?: string;
  vendor_email?: string;
  vendor_details?: VendorDetails;
  created_at: string;
  updated_at: string;
}

const VendorInvoiceDetailPage: React.FunctionComponent = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const [invoice, setInvoice] = useState<VendorInvoice | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [editing, setEditing] = useState<boolean>(false);
  const [extractedTextModalOpen, setExtractedTextModalOpen] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'text' | 'json'>('text');
  const [revalidating, setRevalidating] = useState<boolean>(false);
  const [vendors, setVendors] = useState<VendorDetails[]>([]);
  const [loadingVendors, setLoadingVendors] = useState<boolean>(false);
  const [selectedVendor, setSelectedVendor] = useState<VendorDetails | null>(null);
  const [formData, setFormData] = useState({
    vendor_name: '',
    vendor_id: '',
    location: '',
    invoice_number: '',
    invoice_date: '',
    total_amount: '',
    account_number: '',
    notes: '',
  });

  const fetchVendors = async (searchTerm = '') => {
    setLoadingVendors(true);
    try {
      const response: any = await axios.get('/vendors/servman_vendors_sync', {
        params: { search: searchTerm }
      });
      setVendors(response.data || []);
    } catch (error: any) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/vendors/vendor_invoices/${id}`);
      const invoiceData = response.data;
      setInvoice(invoiceData);
      setFormData({
        vendor_name: invoiceData.vendor_name || '',
        vendor_id: invoiceData.vendor_id || '',
        location: invoiceData.location || '',
        invoice_number: invoiceData.invoice_number || '',
        invoice_date: invoiceData.invoice_date || '',
        total_amount: invoiceData.total_amount?.toString() || '',
        account_number: invoiceData.account_number || '',
        notes: invoiceData.notes || '',
      });
      if (invoiceData.vendor_details) {
        setSelectedVendor(invoiceData.vendor_details);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch invoice details';
      showErrorSnackBar(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVendorChange = (_event: any, newValue: VendorDetails | null) => {
    setSelectedVendor(newValue);
    if (newValue) {
      setFormData({
        ...formData,
        vendor_id: newValue.vendor_id,
        vendor_name: newValue.vendor_name,
        location: newValue.location,
      });
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`/vendors/vendor_invoices/${id}`, {
        vendor_invoice: formData,
      });
      showSuccessSnackBar('Invoice updated successfully');
      setEditing(false);
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to update invoice';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      await axios.delete(`/vendors/vendor_invoices/${id}`);
      showSuccessSnackBar('Invoice deleted successfully');
      history.push('/vendors/invoices');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to delete invoice';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleApproveInvoice = async () => {
    try {
      await axios.put(`/vendors/vendor_invoices/${id}/validate`, {
        validation_status: 'approved',
        validation_notes: 'Approved via invoice detail page',
      });
      showSuccessSnackBar('Invoice approved successfully');
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to approve invoice';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleMarkAsIgnored = async () => {
    try {
      await axios.put(`/vendors/vendor_invoices/${id}/validate`, {
        validation_status: 'ignored',
        validation_notes: 'Marked as ignored via invoice detail page',
      });
      showSuccessSnackBar('Invoice marked as ignored');
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to mark invoice as ignored';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleRevalidate = async () => {
    setRevalidating(true);
    try {
      const response: any = await axios.post(`/vendors/vendor_invoices/${id}/revalidate`);
      showSuccessSnackBar(response.data.message || 'Invoice re-validated successfully');
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Failed to re-validate invoice';
      showErrorSnackBar(errorMessage);
    } finally {
      setRevalidating(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
    fetchVendors();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box>
        <Typography variant="h6">Invoice not found</Typography>
      </Box>
    );
  }

  return (
    <Box style={{ width: '100%' }}>
      {/* Header */}
      <Grid container spacing={2} style={{ marginBottom: 20 }}>
        <Grid item>
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/vendors/invoices')}>
            Back to List
          </Button>
        </Grid>
      </Grid>

      {/* Condensed Invoice Summary */}
      <Card style={{ marginBottom: 24 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h5" component="h1" gutterBottom>
                {invoice.vendor_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Invoice #{invoice.invoice_number}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              {!editing && (
                <Button variant="contained" color="primary" onClick={() => setEditing(true)} size="small">
                  Edit Details
                </Button>
              )}
              {invoice.pdf_url && (
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdfIcon />}
                  href={invoice.pdf_url}
                  target="_blank"
                  size="small"
                >
                  View PDF
                </Button>
              )}
              {invoice.raw_extracted_text && (
                <Button
                  variant="outlined"
                  startIcon={<TextSnippetIcon />}
                  onClick={() => setExtractedTextModalOpen(true)}
                  size="small"
                >
                  View Extracted Text
                </Button>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {editing ? (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={vendors}
                    getOptionLabel={(option) => `${option.vendor_name} (${option.vendor_id})`}
                    value={selectedVendor}
                    onChange={handleVendorChange}
                    loading={loadingVendors}
                    onInputChange={(_, newInputValue) => {
                      if (newInputValue.length > 2) {
                        fetchVendors(newInputValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Vendor Lookup"
                        size="small"
                        helperText="Search for vendor by name"
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.vendor_id === value.vendor_id}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Invoice Number"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Invoice Date"
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Total Amount"
                    type="number"
                    value={formData.total_amount}
                    onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                    fullWidth
                    size="small"
                    InputProps={{ startAdornment: '$' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Account Number"
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                  />
                </Grid>
              </Grid>
              <Box display="flex" gap={2} mt={3}>
                <Button variant="contained" color="primary" onClick={handleUpdate}>
                  Save Changes
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditing(false);
                    setFormData({
                      vendor_name: invoice.vendor_name || '',
                      vendor_id: invoice.vendor_id || '',
                      location: invoice.location || '',
                      invoice_number: invoice.invoice_number || '',
                      invoice_date: invoice.invoice_date || '',
                      total_amount: invoice.total_amount?.toString() || '',
                      account_number: invoice.account_number || '',
                      notes: invoice.notes || '',
                    });
                    setSelectedVendor(invoice.vendor_details || null);
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Invoice Date
                </Typography>
                <Typography variant="body1">
                  {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total Amount
                </Typography>
                <Typography variant="h6" color="primary">
                  {invoice.total_amount
                    ? `$${parseFloat(invoice.total_amount.toString()).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`
                    : 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Account Number
                </Typography>
                <Typography variant="body1">{invoice.account_number || 'N/A'}</Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Line Items
                </Typography>
                <Typography variant="body1">
                  {invoice.line_items && invoice.line_items.length > 0 ? (
                    <Chip label={`${invoice.line_items.length} items`} size="small" color="default" />
                  ) : (
                    'None'
                  )}
                </Typography>
              </Grid>

              {invoice.notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Notes
                  </Typography>
                  <Typography variant="body2">{invoice.notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}

          {invoice.vendor_details && !editing && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box mb={2}>
                <Typography variant="h6" gutterBottom>
                  Vendor Details
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Vendor Name
                  </Typography>
                  <Typography variant="body1">{invoice.vendor_details.vendor_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Vendor ID
                  </Typography>
                  <Typography variant="body1">{invoice.vendor_details.vendor_id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Phone
                  </Typography>
                  <Typography variant="body1">{invoice.vendor_details.phone || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Email
                  </Typography>
                  <Typography variant="body1">{invoice.vendor_details.email || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {[
                      invoice.vendor_details.address1,
                      invoice.vendor_details.address2,
                      [invoice.vendor_details.city, invoice.vendor_details.state, invoice.vendor_details.zip]
                        .filter(Boolean)
                        .join(', '),
                    ]
                      .filter(Boolean)
                      .join(', ') || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>

      {/* Extracted Text Modal */}
      <Dialog open={extractedTextModalOpen} onClose={() => setExtractedTextModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6">Extracted Text</Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant={viewMode === 'text' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setViewMode('text')}
                >
                  View as Text
                </Button>
                <Button
                  variant={viewMode === 'json' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setViewMode('json')}
                  disabled={!invoice.textract_response}
                >
                  View as JSON
                </Button>
              </Box>
            </Box>
            <IconButton edge="end" onClick={() => setExtractedTextModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            value={
              viewMode === 'text'
                ? invoice.raw_extracted_text || 'No extracted text available'
                : invoice.textract_response
                  ? JSON.stringify(invoice.textract_response, null, 2)
                  : 'No JSON data available'
            }
            fullWidth
            multiline
            rows={20}
            variant="outlined"
            InputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtractedTextModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Validations Section */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5" component="h2" fontWeight="600" gutterBottom>
                Invoice Validations
              </Typography>
              {invoice.validated_at && (
                <Typography variant="caption" color="text.secondary">
                  Last validated: {new Date(invoice.validated_at).toLocaleString()}
                </Typography>
              )}
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* Validation Check 1: System Matching */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  {invoice.servman_matched && invoice.docuware_matched ? (
                    <CheckCircleIcon color="success" sx={{ mt: 0.5 }} />
                  ) : !invoice.servman_matched && !invoice.docuware_matched ? (
                    <ErrorIcon color="error" sx={{ mt: 0.5 }} />
                  ) : (
                    <WarningIcon color="warning" sx={{ mt: 0.5 }} />
                  )}
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                      System Matching Status
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {invoice.servman_matched ? (
                            <CheckCircleIcon fontSize="small" color="success" />
                          ) : (
                            <ErrorIcon fontSize="small" color="error" />
                          )}
                          <Typography variant="body2">
                            <strong>ServMan:</strong> {invoice.servman_matched ? 'Found' : 'Not Found'}
                          </Typography>
                        </Box>
                        {invoice.servman_data && (
                          <Box ml={4} mt={1}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Database: {invoice.servman_data.database}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Amount: ${invoice.servman_data.invoice_amount}
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {invoice.docuware_matched ? (
                            <CheckCircleIcon fontSize="small" color="success" />
                          ) : (
                            <ErrorIcon fontSize="small" color="error" />
                          )}
                          <Typography variant="body2">
                            <strong>DocuWare:</strong> {invoice.docuware_matched ? 'Found' : 'Not Found'}
                          </Typography>
                        </Box>
                        {invoice.docuware_data && (
                          <Box ml={4} mt={1}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Doc ID: {invoice.docuware_data.docid}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Amount: ${invoice.docuware_data.invoice_amount}
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Validation Check 2: Vendor Contact Information */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  {invoice.vendor_email && invoice.vendor_email.trim() !== '' ? (
                    <CheckCircleIcon color="success" sx={{ mt: 0.5 }} />
                  ) : (
                    <WarningIcon color="warning" sx={{ mt: 0.5 }} />
                  )}
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                      Vendor Contact Information
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      {invoice.vendor_email && invoice.vendor_email.trim() !== '' ? (
                        <>
                          <CheckCircleIcon fontSize="small" color="success" />
                          <Typography variant="body2" color="success.main">
                            Email available: {invoice.vendor_email}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <WarningIcon fontSize="small" color="warning" />
                          <Typography variant="body2" color="warning.main">
                            No email address on file
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Discrepancies List */}
            {invoice.discrepancies && invoice.discrepancies.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="error">
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Validation Discrepancies Found:</strong>
                  </Typography>
                  <List dense>
                    {invoice.discrepancies.map((discrepancy: string, index: number) => (
                      <ListItem key={index} disableGutters>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <ErrorIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText primary={discrepancy} />
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              </Grid>
            )}

            {/* Overall Validation Status */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" color="text.secondary">
                  Overall Validation Status:
                </Typography>
                <Chip
                  label={
                    invoice.validation_status === 'matched'
                      ? 'Fully Matched'
                      : invoice.validation_status === 'discrepancy'
                        ? 'Has Discrepancies'
                        : invoice.validation_status === 'unmatched'
                          ? 'Not Found in Systems'
                          : 'Pending Validation'
                  }
                  color={
                    invoice.validation_status === 'matched'
                      ? 'success'
                      : invoice.validation_status === 'discrepancy'
                        ? 'warning'
                        : invoice.validation_status === 'unmatched'
                          ? 'error'
                          : 'default'
                  }
                  icon={
                    invoice.validation_status === 'matched' ? (
                      <CheckCircleIcon />
                    ) : invoice.validation_status === 'discrepancy' ? (
                      <WarningIcon />
                    ) : invoice.validation_status === 'unmatched' ? (
                      <ErrorIcon />
                    ) : (
                      <InfoIcon />
                    )
                  }
                />
              </Box>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" gap={2} justifyContent="space-between">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleRevalidate}
                  disabled={revalidating}
                  startIcon={revalidating ? <CircularProgress size={20} /> : undefined}
                >
                  {revalidating ? 'Re-validating...' : 'Re-validate Invoice'}
                </Button>
                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleApproveInvoice}
                  >
                    Approve Invoice
                  </Button>
                  <Button variant="outlined" color="warning" onClick={handleMarkAsIgnored}>
                    Mark As Ignored
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VendorInvoiceDetailPage;
